/* eslint-disable max-len */
import type { FValues, Gradients } from ".";
import { DunsRosFlowRegimes } from ".";
import type { PipeFrictionContext } from "../d";
import { calculateGradientsBubble } from "./gradients_BUBBLE";
import { calculateGradientsLiquid } from "./gradients_LIQUID";
import { calculateGradientsMist } from "./gradients_MIST";
import { calculateGradientsSlug } from "./gradients_SLUG";

export type DunsRosGradients = {
  slipCorrelation: number;
  frictionGradient: number;
  liquidHoldup: number;
  staticGradient: number;
  intermediates: {
    [key: string]: number;
  };
};

function linearRegression(
  x: number,
  known_y: number[],
  known_x: number[]
): number {
  const n = known_y.length;
  let sum_x = 0;
  let sum_y = 0;
  let sum_xy = 0;
  let sum_xx = 0;

  for (let i = 0; i < n; i++) {
    sum_x += known_x[i];
    sum_y += known_y[i];
    sum_xy += known_x[i] * known_y[i];
    sum_xx += known_x[i] * known_x[i];
  }

  const slope = (n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);
  const intercept = (sum_y - slope * sum_x) / n;

  return slope * x + intercept;
}

export function getF(
  liquidViscosityNumber: number,
  yF: number[],
  x: number[],
  upperbound: number,
  lowerbound: number
): number {
  const forecast = linearRegression(liquidViscosityNumber, yF, x);

  if (forecast > upperbound) {
    return upperbound;
  } else if (forecast < lowerbound) {
    return lowerbound;
  } else {
    return forecast;
  }
}

export function calculateGradients(context: PipeFrictionContext): Gradients {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        gasSuperficialVelocityFTSEC,
        gasDensityLBMFT3,
        liquidSuperficialVelocityFTSEC,
        liquidDensityLBMFT3
      },
      // flowRatesInSitu,
      fluidFrictionProperties: {
        pipeDiameterNumber,
        flowRegime,
        liquidViscosityNumber
      }
    },
    P
  } = context;

  const yF1 = [1.1, 1.2, 1.25, 1.97, 2, 0.9];
  const yF2 = [0.22, 0.25, 0.34, 0.8, 0.9, 1];
  const yF3 = [0.83, 2, 2.25, 3.15, 3.5, 4];
  const yF4 = [-20, 28.5, 41, 55, 55, 55];
  const yF5 = [
    0.22, 0.2, 0.19, 0.16, 0.122, 0.07, 0.045, 0.04, 0.07, 0.08, 0.12
  ];
  const yF6 = [0.95, 0.2, -0.025, -0.17, 1, 1.85, 2.18, 2.08, 1.8, 1.7, 1.78];
  const yF7 = [
    0.13, 0.11, 0.094, 0.07, 0.05, 0.038, 0.034, 0.032, 0.027, 0.025, 0.024
  ];

  const x1 = [0.002, 0.02, 0.03, 0.1, 0.2, 2];
  const x2 = [0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.12, 0.15, 0.5, 1, 2];

  const F1 = getF(liquidViscosityNumber, yF1, x1, 2, 0.9);
  const F2 = getF(liquidViscosityNumber, yF2, x1, 1, 0.22);
  const F3 = getF(liquidViscosityNumber, yF3, x1, 4, 0.83);
  const F4 = getF(liquidViscosityNumber, yF4, x1, 55, -20);
  const F5 = getF(liquidViscosityNumber, yF5, x2, 0.22, 0.04);
  const F6 = getF(liquidViscosityNumber, yF6, x2, 2.18, -0.025);
  const F7 = getF(liquidViscosityNumber, yF7, x2, 0.13, 0.024);

  const F3tick = F3 - F4 / pipeDiameterNumber;
  const F6tick = 0.029 * pipeDiameterNumber + F6;

  const fValues: FValues = { F1, F2, F3, F3tick, F4, F5, F6, F6tick, F7 };

  const fallbackGradients: Gradients = {
    staticGradient: 0,
    frictionGradient: 0,
    transitionGradient: 0,
    slipCorrelation: 0,
    liquidHoldup: 0
  };

  let gradients: Gradients = { ...fallbackGradients };
  if (flowRegime === DunsRosFlowRegimes.LIQUID) {
    gradients = calculateGradientsLiquid(context, fValues);
  } else if (flowRegime === DunsRosFlowRegimes.BUBBLE) {
    gradients = calculateGradientsBubble(context, fValues);
  } else if (flowRegime === DunsRosFlowRegimes.SLUG) {
    gradients = calculateGradientsSlug(context, fValues);
  } else if (flowRegime === DunsRosFlowRegimes.MIST) {
    gradients = calculateGradientsMist(context);
    const { staticGradient, frictionGradient } = gradients;
    if (
      !Number.isFinite(staticGradient) ||
      !Number.isFinite(frictionGradient)
    ) {
      // If the static or friction gradient is not finite, we cannot proceed with the transition calculation.
      return fallbackGradients;
    }

    // D311 =(D299+D291)/(1-(D63*D150+D62*D149)*(D149/D10))
    const transitionGradient =
      (staticGradient + frictionGradient) /
      (1 -
        (liquidDensityLBMFT3 * liquidSuperficialVelocityFTSEC +
          gasDensityLBMFT3 * gasSuperficialVelocityFTSEC) *
          (gasSuperficialVelocityFTSEC / P));
    if (!Number.isFinite(transitionGradient)) {
      return fallbackGradients;
    }

    gradients = {
      ...gradients,
      transitionGradient
    };
  } else {
    // If we reach this point, the flow regime is not recognized, or dunsRos
    // does not have a specific calculation for it, so we use liquid.
    gradients = gradients = calculateGradientsLiquid(context, fValues);
  }
  return gradients;
}
